import {
  BrandfolderHexColors,
  CodeEditorLanguages,
  StandardCodeEditor,
  StandardCodeEditorProps
} from '@brandfolder/react';
import { FunctionComponent, useContext, useState } from 'react';
import styled from 'styled-components';

import { BlockContext } from '@context/blocks';
import { DragAndDropContext } from '@context/drag-and-drop';
import { PublishContext } from '@context/publish';
import { Block } from '@typings/block';
import { getBlockDataIsHtml, updateBlock } from '@utilities/block';
import { ADD_ATTR, ADD_TAGS, ALLOWED_IFRAME_DOMAINS } from '@utilities/sanitize';

interface CodeEditorProps extends Omit<StandardCodeEditorProps, 'id' | 'label'> {
  block: Block;
  label: string;
  language: CodeEditorLanguages;
}

const UnstyledCodeEditor: FunctionComponent<CodeEditorProps> = (props) => {
  const { block, label, language, ...otherProps } = props;
  const { data, key } = block;

  const { blocks, setBlocks } = useContext(BlockContext);
  const { dragging, reordering } = useContext(DragAndDropContext);
  const { setPublish } = useContext(PublishContext);

  const [blurred, setBlurred] = useState(false);

  if (!getBlockDataIsHtml(data)) return null;

  const disabled = dragging || !!reordering;

  return (
    <StandardCodeEditor
      {...otherProps}
      codeEditorProps={{
        sanitizeConfig: {
          ADD_ATTR,
          ADD_TAGS,
          ALLOWED_IFRAME_DOMAINS
        }
      }}
      defaultValue={data.html}
      disabled={disabled}
      id={key}
      label={label}
      language={language}
      onBlur={() => {
        setBlurred(true);
      }}
      onEdit={(code, isSanitized) => {
        if (isSanitized && code === '') {
          setBlocks([
            ...updateBlock(
              {
                ...block,
                data: {
                  ...data,
                  html: '<p style="color: #ea5400;">Invalid HTML sanitized.</p>'
                }
              },
              blocks
            )
          ]);
        } else if (data.html !== code) {
          setBlocks([...updateBlock({ ...block, data: { ...data, html: code } }, blocks)]);
        }

        if (blurred) {
          setPublish(true);
          setBlurred(false);
        }
      }}
    />
  );
};

const StyledCodeEditor = styled(UnstyledCodeEditor)`
  color: ${BrandfolderHexColors.Text};
`;

export const CodeEditor: FunctionComponent<CodeEditorProps> = (props) => {
  return <StyledCodeEditor {...props} />;
};
